import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../../services/api';
import planApi from '../../../services/planApi';
import MainLayout from '../../layout/MainLayout';
import { ArrowRight, Star, Lock, Check } from 'lucide-react';
import './AdminDashboard.scss';

const fmtDate = (sql?: string) => {
  if (!sql) return '—';
  const d = new Date(String(sql).replace(' ', 'T'));
  return isNaN(d.getTime()) ? String(sql) : d.toLocaleDateString('es', { dateStyle: 'medium' });
};

const eyebrowDate = () => {
  const d = new Date();
  const dia = d.toLocaleDateString('es', { weekday: 'long' });
  const mes = d.toLocaleDateString('es', { month: 'short' }).replace('.', '');
  return `${dia} ${d.getDate()} de ${mes} · Administrador`.toUpperCase();
};

interface Overview {
  program: { month_index: number; total_months: number; month_label: string };
  kpis: { active_pairs: number; sessions_done: number; sessions_total: number; fodas_done: number; pdis_done: number; duplas_total: number };
  this_month: { label: string; sessions: number; duplas: number };
  rhythm: { idx: number; label: string; count: number }[];
  pdi: { avg: number; duplas: { name: string; progress: number }[] };
  ratings: { mentee_avg: number; mentor_avg: number; at_risk: number };
  inbox: { unread: number; total: number };
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ov, setOv] = useState<Overview | null>(null);
  const [feedback, setFeedback] = useState<any[]>([]);

  useEffect(() => { loadDashboardData(); /* eslint-disable-next-line */ }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [ovResp, fbResp] = await Promise.all([
        planApi.getAdminOverview().catch(() => null),
        apiService.get('/admin/session-feedback').catch(() => null),
      ]);
      setOv(ovResp as Overview);
      setFeedback(fbResp?.data?.data?.sessions || []);
    } catch (error) {
      console.error('Error loading admin dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !ov) {
    return (
      <MainLayout>
        <div className="admin-dashboard admin-dashboard--loading">
          <div className="loading-spinner">Cargando panel…</div>
        </div>
      </MainLayout>
    );
  }

  const year = new Date().getFullYear();
  const maxRhythm = Math.max(1, ...ov.rhythm.map(r => r.count));

  return (
    <MainLayout>
      <div className="admin-dashboard">
        {/* ---- Cabecera ---- */}
        <div className="ov-header">
          <div className="eyebrow">{eyebrowDate()}</div>
          <h1 className="ov-title">Programa de <em>mentoría</em> {year}</h1>
          <p className="ov-sub">
            Mes {ov.program.month_index} de {ov.program.total_months} · {ov.kpis.active_pairs} duplas activas. La salud del programa de un vistazo.
          </p>
        </div>

        {/* ---- KPIs ---- */}
        <div className="ov-kpis">
          <div className="ov-kpi">
            <div className="ov-kpi__num">{ov.kpis.active_pairs}</div>
            <div className="ov-kpi__lbl">Duplas activas</div>
          </div>
          <div className="ov-kpi">
            <div className="ov-kpi__num">{ov.kpis.sessions_done}<span className="ov-kpi__den">/{ov.kpis.sessions_total}</span></div>
            <div className="ov-kpi__lbl">Sesiones realizadas</div>
          </div>
          <div className="ov-kpi">
            <div className="ov-kpi__num">{ov.kpis.fodas_done}<span className="ov-kpi__den">/{ov.kpis.duplas_total}</span></div>
            <div className="ov-kpi__lbl">FODAs hechos</div>
          </div>
          <div className="ov-kpi">
            <div className="ov-kpi__num">{ov.kpis.pdis_done}<span className="ov-kpi__den">/{ov.kpis.duplas_total}</span></div>
            <div className="ov-kpi__lbl">PDIs definidos</div>
          </div>
        </div>

        {/* ---- Este mes ---- */}
        <div className="ov-card">
          <div className="ov-card__head">
            <h2 className="ov-card__title">Este mes · <em>{ov.this_month.label}</em></h2>
            <button className="ov-link" onClick={() => navigate('/admin/scheduling')}>ver calendario →</button>
          </div>
          <p className="ov-thismonth">
            {ov.this_month.duplas > 0
              ? `${ov.this_month.duplas} ${ov.this_month.duplas === 1 ? 'dupla agendó su sesión del mes' : 'duplas agendaron su sesión del mes'}.`
              : `Ninguna dupla ha agendado su sesión de ${ov.this_month.label} aún.`}
          </p>
        </div>

        {/* ---- Ritmo de sesiones ---- */}
        <div className="ov-card">
          <div className="ov-card__head">
            <h2 className="ov-card__title">Ritmo de <em>sesiones</em></h2>
            <button className="ov-link" onClick={() => navigate('/admin/scheduling')}>
              {ov.kpis.sessions_done + (ov.kpis.sessions_done === 1 ? ' sesión' : ' sesiones')} · ver agendamiento →
            </button>
          </div>
          <div className="ov-rhythm">
            {ov.rhythm.map(r => (
              <div className="ov-bar" key={r.idx} title={`${r.label}: ${r.count}`}>
                <span className={`ov-bar__num ${r.count === 0 ? 'is-zero' : ''}`}>{r.count}</span>
                <div className="ov-bar__track">
                  <div className="ov-bar__fill" style={{ height: `${(r.count / maxRhythm) * 100}%` }} />
                </div>
                <span className="ov-bar__lbl">M{r.idx}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ---- Avance del PDI ---- */}
        <div className="ov-card ov-card--accent">
          <div className="ov-card__head">
            <h2 className="ov-card__title">Avance del <em>PDI</em></h2>
            <button className="ov-link" onClick={() => navigate('/admin/pdi-progress')}>promedio {ov.pdi.avg}% · ver avance →</button>
          </div>
          {ov.pdi.duplas.length === 0 ? (
            <p className="ov-empty">No hay duplas activas.</p>
          ) : (
            <div className="ov-pdi">
              {ov.pdi.duplas.map((d, i) => (
                <div className="ov-pdi__row" key={i}>
                  <span className="ov-pdi__name">{d.name}</span>
                  <span className="ov-pdi__bar"><span className="ov-pdi__fill" style={{ width: `${d.progress}%` }} /></span>
                  <span className="ov-pdi__pct">{d.progress}%</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ---- Calificaciones + Buzón ---- */}
        <div className="ov-two">
          <div className="ov-card">
            <div className="ov-card__head">
              <h2 className="ov-card__title">Calificaciones</h2>
              <button className="ov-link" onClick={() => navigate('/admin/ratings')}>ver →</button>
            </div>
            <div className="ov-metrics">
              <div className="ov-metric">
                <div className="ov-metric__num ov-metric__num--green">{ov.ratings.mentee_avg}<Star size={20} fill="currentColor" /></div>
                <div className="ov-metric__lbl">Promedio mentee</div>
              </div>
              <div className="ov-metric">
                <div className="ov-metric__num ov-metric__num--green">{ov.ratings.mentor_avg}<Star size={20} fill="currentColor" /></div>
                <div className="ov-metric__lbl">Promedio mentor</div>
              </div>
              <div className="ov-metric">
                <div className="ov-metric__num ov-metric__num--red">{ov.ratings.at_risk}</div>
                <div className="ov-metric__lbl">En riesgo de match</div>
              </div>
            </div>
          </div>

          <div className="ov-card">
            <div className="ov-card__head">
              <h2 className="ov-card__title">Buzón</h2>
              <button className="ov-link" onClick={() => navigate('/mensajeria')}>ver mensajería →</button>
            </div>
            <div className="ov-metrics">
              <div className="ov-metric">
                <div className="ov-metric__num ov-metric__num--red">{ov.inbox.unread}</div>
                <div className="ov-metric__lbl">Sin leer</div>
              </div>
              <div className="ov-metric">
                <div className="ov-metric__num">{ov.inbox.total}</div>
                <div className="ov-metric__lbl">En el buzón</div>
              </div>
            </div>
          </div>
        </div>

        {/* ---- Registro de sesiones (feedback confidencial) ---- */}
        <div className="ov-card">
          <div className="ov-card__head">
            <h2 className="ov-card__title">Registro de <em>sesiones</em></h2>
            <button className="ov-link" onClick={() => navigate('/admin/ratings')}>ver calificaciones →</button>
          </div>
          <p className="fb-note"><Lock size={13} /> Los comentarios entre mentor y mentee son confidenciales — no se muestran. Aquí ves qué sesiones tienen registro y las notas dirigidas al equipo.</p>
          {feedback.length === 0 ? (
            <p className="feedback-empty">Aún no hay sesiones registradas.</p>
          ) : (
            <div className="fb-list">
              {feedback.map((s: any) => (
                <div className="fb-item" key={s.id}>
                  <div className="fb-item__top">
                    <div className="fb-item__dupla">
                      <strong>{s.mentee_first_name} {s.mentee_last_name}</strong>
                      <span className="fb-item__arrow">↔</span>
                      <span className="fb-item__mentor">{s.mentor_first_name} {s.mentor_last_name}</span>
                    </div>
                    <span className="fb-item__date">{fmtDate(s.scheduled_date)}</span>
                  </div>
                  {s.objectives?.length > 0 && (
                    <div className="fb-item__obj">{s.objectives.join(' · ')}</div>
                  )}
                  <div className="fb-chips">
                    <span className={`fb-chip ${Number(s.has_mentee_feedback) ? 'on' : 'off'}`}>
                      {Number(s.has_mentee_feedback) ? <Check size={13} /> : null} Mentee {Number(s.has_mentee_feedback) ? 'comentó' : 'sin comentario'}
                    </span>
                    <span className={`fb-chip ${Number(s.has_mentor_feedback) ? 'on' : 'off'}`}>
                      {Number(s.has_mentor_feedback) ? <Check size={13} /> : null} Mentor {Number(s.has_mentor_feedback) ? 'comentó' : 'sin comentario'}
                    </span>
                    {!!s.session_rating && <span className="fb-rate">Mentee {'★'.repeat(Math.round(s.session_rating))}</span>}
                    {!!s.mentor_rating && <span className="fb-rate">Mentor {'★'.repeat(Math.round(s.mentor_rating))}</span>}
                  </div>
                  {s.meeting_notes && (
                    <div className="fb-adminnote"><span className="fb-adminnote__label">Nota al equipo del programa</span>“{s.meeting_notes}”</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="ov-footer-action">
          <button className="btn btn--primary" onClick={() => navigate('/admin/relationships')}>
            Ver todas las duplas <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </MainLayout>
  );
};

export default AdminDashboard;
